import { useCallback, useContext, useEffect, useState } from "react";
import AuthContext from "../store/AuthContext";
import type { ILocalPreferences, ITableFiltersOnly } from "../interfaces/models/IUserPreferences";
import {
  getLocalPreferences,
  setLocalPreferences as setLocalPreferencesStorage,
  getTableFilters,
  setTableFilters as setTableFiltersStorage,
} from "../utils/localPreferences";

export function useLocalPreferences() {
  const { tokenDecoded, loggedPerson } = useContext(AuthContext);
  const userId = tokenDecoded?.oid ?? loggedPerson?.id ?? undefined;

  const [localPrefs, setLocalPrefsState] = useState<ILocalPreferences>(() =>
    userId ? getLocalPreferences(userId) : {}
  );

  useEffect(() => {
    if (!userId) {
      setLocalPrefsState({});
      return;
    }
    setLocalPrefsState(getLocalPreferences(userId));
  }, [userId]);

  const setLocalPreferences = useCallback(
    (update: Partial<ILocalPreferences> | ((prev: ILocalPreferences) => ILocalPreferences)) => {
      if (!userId) {
        return;
      }
      setLocalPrefsState((prev) => {
        const next = typeof update === "function" ? update(prev) : { ...prev, ...update };
        setLocalPreferencesStorage(userId, next);
        return next;
      });
    },
    [userId]
  );

  const getTableFiltersForTable = useCallback(
    (tableKey: string) => getTableFilters(userId, tableKey),
    [userId]
  );

  const setTableFiltersForTable = useCallback(
    (tableKey: string, partial: ITableFiltersOnly) => {
      if (!userId) {
        return;
      }
      setTableFiltersStorage(userId, tableKey, partial);
      setLocalPrefsState((prev) => ({
        ...prev,
        tableFilters: {
          ...(prev.tableFilters ?? {}),
          [tableKey]: { ...(prev.tableFilters?.[tableKey] ?? {}), ...partial },
        },
      }));
    },
    [userId]
  );

  const tableFilters = localPrefs?.tableFilters ?? {};

  return {
    localPrefs,
    tableFilters,
    getTableFilters: getTableFiltersForTable,
    setTableFilters: setTableFiltersForTable,
    setLocalPreferences,
    userId,
  };
}
